import { hasOwn } from './domain.js';
import { fxFactor } from './cost.js';
/** Generic hierarchy level keys, and the design-only target levels. */
export const LEVEL_KEY_RE = /^lvl\d+$/;
export const TARGET_LEVEL_KEY_RE = /^tlvl\d+$/;
export function isLevelKey(key) {
    return LEVEL_KEY_RE.test(key);
}
export function isTargetLevelKey(key) {
    return TARGET_LEVEL_KEY_RE.test(key);
}
/** Mapped logical fields, in the order the original's COLUMN_KEYS declares them. */
export const COLUMN_KEYS = [
    'name', 'id', 'supervisorId', 'fte', 'location', 'jobTitle', 'businessUnit',
    'legalEntity', 'country', 'salary', 'seniority', 'openPosition', 'inactive', 'dedication',
    'currency', 'pensionScheme',
];
/**
 * The mapped fields with their user-facing names.
 *
 * The single-file app carries `patterns` here too — the header spellings it guesses a mapping from.
 * They live in `column-guess.ts` rather than here, because this table is about how a mapped field
 * is RESOLVED and that one is about how a mapping is first proposed. The guess is only ever a
 * starting point: what it produces is an ordinary `columnMap` the user edits through
 * `setColumnMapping` like any other.
 *
 * They were once not ported at all, on the reasoning that the platform never guesses because it
 * only ingests exports from the app, which already carry a mapping somebody confirmed. That was
 * true, and it was also why a project could only be born in the frozen app. It stopped being true
 * when the platform learned to ingest a plain client roster.
 */
export const COLUMN_FIELDS = [
    { key: 'name', label: 'Name', required: true },
    { key: 'id', label: 'Personnel ID', required: true },
    { key: 'supervisorId', label: 'Supervisor ID', required: false },
    { key: 'fte', label: 'FTE', required: false },
    { key: 'location', label: 'Location', required: false },
    { key: 'jobTitle', label: 'Job Title', required: false },
    { key: 'businessUnit', label: 'Business Unit', required: false },
    { key: 'legalEntity', label: 'Legal Entity', required: false },
    { key: 'country', label: 'Country', required: false },
    { key: 'salary', label: 'Salary', required: false },
    { key: 'seniority', label: 'Seniority', required: false },
    { key: 'openPosition', label: 'Open Position', required: false },
    { key: 'inactive', label: 'Inactive', required: false },
    { key: 'dedication', label: 'Dedication (carve-out share)', required: false },
    /*
      APPENDED, never inserted: `suggestColumnMap` assigns headers greedily in this order, so a new
      field placed earlier could steal a contested header from a field that has always won it, and
      every existing project's auto-mapping would quietly shift.
    */
    { key: 'currency', label: 'Currency', required: false },
    { key: 'pensionScheme', label: 'Pension Scheme', required: false },
];
const ATTRIBUTE_READERS = {
    id: (p) => p.id,
    name: (p) => p.name,
    supervisorId: (p) => p.supervisorId ?? '',
    jobTitle: (p) => p.jobTitle ?? '',
    location: (p) => p.location ?? '',
    country: (p) => p.country ?? '',
    seniority: (p) => p.seniority ?? '',
    businessUnit: (p) => p.businessUnit ?? '',
    legalEntity: (p) => p.legalEntity ?? '',
    salary: (p) => p.salary,
    fte: (p) => p.fte,
    dedication: (p) => p.dedication,
    currency: (p) => p.currency ?? '',
    pensionScheme: (p) => p.pensionScheme ?? '',
};
/** The stored value, before any scenario overlay. */
export function baseFieldValue(person, key) {
    if (isLevelKey(key) || isTargetLevelKey(key)) {
        return hasOwn(person.levels, key) ? person.levels[key] : undefined;
    }
    const reader = ATTRIBUTE_READERS[key];
    return reader ? reader(person) : undefined;
}
/**
 * Resolve one logical field, applying the active scenario's overlays.
 *
 * `as-is` is always the untouched base record: the whole point of a scenario is that it does not
 * mutate the imported data.
 */
export function getEmpField(person, key, scenario, overrides) {
    if (scenario === 'as-is')
        return baseFieldValue(person, key);
    const ov = overrides?.[scenario];
    if (!ov)
        return baseFieldValue(person, key);
    const perPerson = ov.empOverrides?.[person.id];
    if (perPerson && hasOwn(perPerson, key))
        return perPerson[key];
    const original = baseFieldValue(person, key);
    const renames = ov.groupRenames?.[key];
    if (renames && hasOwn(renames, String(original)))
        return renames[String(original)];
    return original;
}
/**
 * A per-person weight, resolved through a PERSPECTIVE — the metric a group card measures with.
 *
 * `metricFn` (economics.ts) reads `p.salary` and `p.fte` straight off the record, which is right for
 * `computeStageBridge`: that function spans every bucket at once, so there is no single perspective to
 * resolve through. Anything scoped to ONE perspective must read through `getEmpField` instead, or a
 * per-scenario salary or FTE override is invisible to it — the group card beside it applies the
 * override and the pane does not, and the two disagree about the same group.
 *
 * A function rather than a fifth hand-retyped expression: `hierarchy.ts` has it twice for group cards
 * and once for the reporting rollup, and `packages/export/src/deck-tree.ts` twice more. The frozen app
 * had the same duplication for its cost weight and it produced a real defect — the deck printing loaded
 * cost under a footnote that says raw salary — which is why `metricFn` exists at all.
 */
export function resolvedMetricFn(design, perspective, key, assumptions) {
    if (key === 'hc')
        return () => 1;
    // `cost` is RAW annual salary here, exactly as `metricFn('cost')` is: the perspective changes WHICH
    // salary is read, never whether the loaded multiplier is applied. The FX factor keys off the
    // PERSON's currency even when an override supplies the amount — an override on a GBP person is
    // still a GBP amount (cost.ts states the rule).
    const field = key === 'fte' ? 'fte' : 'salary';
    if (key === 'cost' && assumptions !== null) {
        return (p) => (Number(getEmpField(p, field, perspective, design.scenarioOverrides)) || 0) * fxFactor(p, assumptions);
    }
    return (p) => Number(getEmpField(p, field, perspective, design.scenarioOverrides)) || 0;
}
/**
 * Resolve ANY field by its id, for grouping, filtering and pivots alike.
 *
 * `raw:<header>` reads the untouched sheet value and is deliberately NOT subject to scenario
 * overrides. Target levels (`tlvlN`) always resolve from the target placement whatever the active
 * scenario, so analytics can look at the org through its target units. Status and dedication are
 * exposed as clean facets rather than their stored representation, so the filter and pivot machinery
 * treats them like any other field.
 */
export function resolveField(state, person, fieldId, scenario) {
    if (fieldId.startsWith('raw:')) {
        const header = fieldId.slice(4);
        const raw = person.raw;
        return raw && hasOwn(raw, header) ? String(raw[header] ?? '') : '';
    }
    if (isTargetLevelKey(fieldId)) {
        return getEmpField(person, fieldId, 'target', state.targetOverrides);
    }
    if (fieldId === 'openPosition')
        return person.openPosition ? 'Open' : 'Filled';
    if (fieldId === 'inactive')
        return person.inactive ? 'Inactive' : 'Active';
    // Kept resolvable so saved views and filters from pre-dedication project files keep working.
    if (fieldId === 'splitHead')
        return person.sharedMarker ? 'Yes' : 'No';
    if (fieldId === 'dedication')
        return person.dedication == null ? '' : person.dedication;
    return getEmpField(person, fieldId, scenario, state.design.scenarioOverrides);
}
/**
 * True when a person matches EVERY active condition: AND across fields, OR within one field's
 * selected values, with a min/max range for numeric fields.
 *
 * A blank numeric value never satisfies a range — unknown is not zero. Getting that wrong would
 * quietly pull every person with no salary into a "salary under X" filter.
 */
export function passesFilters(state, person, scenario, filters) {
    if (!filters.length)
        return true;
    for (const f of filters) {
        const persp = f.perspective || scenario;
        if (f.numeric && f.range) {
            const raw = resolveField(state, person, f.fieldId, persp);
            if (raw == null || raw === '')
                return false;
            const n = Number(raw);
            if (Number.isNaN(n))
                return false;
            const hasMin = f.range.min != null && f.range.min !== '';
            const hasMax = f.range.max != null && f.range.max !== '';
            if (hasMin && n < Number(f.range.min))
                return false;
            if (hasMax && n > Number(f.range.max))
                return false;
        }
        else if (f.values && f.values.length) {
            const v = resolveField(state, person, f.fieldId, persp) || `(No ${f.label})`;
            if (!f.values.includes(String(v)))
                return false;
        }
    }
    return true;
}
/**
 * The country / grade values a severance grid can actually be keyed by.
 *
 * Deliberately NOT `getDistinctValues`, and the three differences each produce a tier that is stored,
 * logged, exported and prices nothing:
 *
 *  - it resolves through `resolveField`, so a country relabelled in a to-be scenario yields the NEW
 *    label, while `severanceMonths` matches the RAW `p.country` (`cost.ts:40-42`);
 *  - it walks build-ups as well as employees, and a planned position is never severed;
 *  - it folds blanks into `(No Country)`, and `lookup` returns null for a person with no country
 *    (`cost.ts:48`), so that row could never win.
 *
 * Leavers are excluded because they have already gone: they are outside every aggregate
 * (`countsInAggregates`) and outside the original's own row source (`orgchart.html:6789-6790`).
 *
 * Sorted with `localeCompare` rather than the original's default byte sort — a deliberate divergence,
 * because row order in an editor is not a computed figure and "Ärzte" belongs next to "Arzt". Nothing
 * in the parity harness reads this.
 */
export function assumptionGridKeys(state, field) {
    const set = new Set();
    for (const p of state.employees) {
        if (p.isLeaver)
            continue;
        const value = p[field];
        if (value == null)
            continue;
        const text = String(value);
        if (text.trim() === '')
            continue;
        set.add(text);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
}
/** Sorted distinct values of a field, with blanks folded into the same bucket grouping uses. */
export function getDistinctValues(state, fieldId, label, scenario) {
    const set = new Set();
    for (const p of [...state.employees, ...state.design.buildUps]) {
        set.add(String(resolveField(state, p, fieldId, scenario) || `(No ${label})`));
    }
    return [...set].sort((a, b) => a.localeCompare(b));
}
const NUMERIC_COLUMN_KEYS = new Set(['salary', 'fte', 'dedication']);
const NUMERIC_SAMPLE = 50;
function detectNumeric(people, header) {
    let seen = 0;
    let numeric = 0;
    for (const p of people) {
        const raw = p.raw;
        if (!raw || !hasOwn(raw, header))
            continue;
        const v = raw[header];
        if (v === null || v === undefined || v === '')
            continue;
        seen++;
        if (Number.isFinite(Number(v)))
            numeric++;
        if (seen >= NUMERIC_SAMPLE)
            break;
    }
    return seen > 0 && numeric / seen >= 0.9;
}
export function availableFields(state) {
    const fields = [];
    for (const level of state.groupingLevels) {
        if (state.columnMap[level.key])
            fields.push({ id: level.key, label: level.label, numeric: false });
    }
    if (state.targetEnabled) {
        for (const level of state.targetLevels) {
            fields.push({ id: level.key, label: `Target: ${level.label}`, numeric: false });
        }
    }
    const everybody = [...state.employees, ...state.design.buildUps];
    for (const column of COLUMN_FIELDS) {
        // Dedication is editable per person without a mapped column, so it is offered whenever anybody
        // carries the data — the legacy split-head marker counts, which is why it is tested too.
        const hasDedicationData = column.key === 'dedication' &&
            everybody.some((p) => p.dedication != null || p.sharedMarker);
        if (state.columnMap[column.key] || hasDedicationData) {
            fields.push({
                id: column.key,
                label: column.label,
                numeric: NUMERIC_COLUMN_KEYS.has(column.key),
            });
        }
    }
    const anyRaw = everybody.some((p) => p.raw != null);
    if (anyRaw) {
        const mapped = new Set(Object.values(state.columnMap));
        for (const column of state.excelColumns) {
            if (mapped.has(column))
                continue;
            fields.push({ id: `raw:${column}`, label: column, numeric: detectNumeric(everybody, column) });
        }
    }
    return fields;
}
/**
 * How many conditions actually constrain anything. Ported from `activeFilterCount`
 * (orgchart.html:2952-2957).
 *
 * `filters.length` over-reports: a condition that has been added but not yet given any values matches
 * everybody, so counting it would put a badge on a chart nothing is hiding.
 */
export function activeFilterCount(filters) {
    return filters.filter((f) => (f.values && f.values.length > 0) ||
        (f.numeric &&
            f.range &&
            ((f.range.min != null && f.range.min !== '') || (f.range.max != null && f.range.max !== '')))).length;
}
/* ------------------------------------------------------------------ *
 * Levels and heads
 * ------------------------------------------------------------------ */
/** The hierarchy levels in play for a perspective, minus any this scenario has flattened. */
export function getActiveLevels(state, scenario) {
    const ov = state.design.scenarioOverrides?.[scenario];
    const disabled = scenario !== 'as-is' && ov?.disabledLevels ? ov.disabledLevels : [];
    return state.groupingLevels.filter((l) => !disabled.includes(l.key));
}
/**
 * The designated head of a group in a perspective.
 *
 * `null` stored means "explicitly no head" and must not fall through to inheritance — that
 * distinction is why the map stores nulls rather than deleting keys. Remain inherits the as-is head
 * when that person is still on the Remain side; the carve-out never inherits, because its structure
 * is designed independently.
 */
export function getHeadFor(state, groupId, scenario, bucketReader) {
    const forScenario = state.design.heads?.[scenario];
    if (forScenario && hasOwn(forScenario, groupId)) {
        return forScenario[groupId] ?? null;
    }
    if (scenario === 'remain') {
        const asIs = state.design.heads?.['as-is']?.[groupId];
        if (asIs && bucketReader(asIs) === 'Remain')
            return asIs;
    }
    return null;
}
export const ORG_HEAD_GROUP = 'G:__org__';
/**
 * WHY a group has the head it has, ported from orgchart.html:2633.
 *
 * `getHeadFor` answers who; this answers where the answer came from, which is what the person panel
 * needs to say "inherited" rather than presenting a Remain-side inheritance as a decision somebody made
 * on the Remain side. The three states are the same three the row-presence model encodes:
 *
 *   explicit  — a row for this scenario naming someone
 *   none      — a row naming nobody (an explicit "this group has no head", which must not inherit),
 *               or no row and nothing to inherit
 *   inherited — no row, and the as-is head is still on the Remain side
 */
export function getHeadSource(state, groupId, scenario, bucketReader) {
    const forScenario = state.design.heads?.[scenario];
    if (forScenario && hasOwn(forScenario, groupId)) {
        return forScenario[groupId] == null ? 'none' : 'explicit';
    }
    if (scenario === 'remain') {
        const asIs = state.design.heads?.['as-is']?.[groupId];
        if (asIs && bucketReader(asIs) === 'Remain')
            return 'inherited';
    }
    return 'none';
}
/**
 * The fields a person's panel can edit, ported from orgchart.html:2333.
 *
 * Hierarchy levels first, because editing a level value IS moving the person between groups; then the
 * mapped attributes. Two deliberate exclusions: `id` is identity, and `supervisorId` is edited by moving
 * the person in the reporting tree rather than by typing an id.
 *
 * Dedication is the one field offered even when no column maps to it. Clients frequently cannot supply
 * it up front, and it is what the perimeter rule runs on, so it has to be enterable person by person.
 */
export function editableFields(state) {
    const columnMap = state.columnMap ?? {};
    const levels = state.groupingLevels
        .filter((l) => columnMap[l.key])
        .map((l) => ({ key: l.key, label: l.label, numeric: false }));
    const attrs = COLUMN_FIELDS.filter((c) => c.key !== 'id' && c.key !== 'supervisorId' && (columnMap[c.key] || c.key === 'dedication')).map((c) => ({
        key: c.key,
        label: c.label,
        numeric: c.key === 'salary' || c.key === 'fte' || c.key === 'dedication',
    }));
    return [...levels, ...attrs];
}
/**
 * The values already in use for a field, for a type-ahead, ported from orgchart.html:2382.
 *
 * Through `resolveField` rather than the stored value, so status fields suggest "Open" / "Filled" —
 * which is what the panel shows and therefore what someone would type.
 */
export function fieldSuggestions(state, key, scenario, extra = []) {
    const seen = new Set();
    for (const person of [...state.employees, ...extra]) {
        const value = resolveField(state, person, key, scenario);
        if (value !== '' && value != null)
            seen.add(String(value));
    }
    return [...seen].sort((a, b) => a.localeCompare(b));
}
//# sourceMappingURL=fields.js.map